if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      swiperInstance = null; // 新增：存储Swiper实例，避免重复初始化

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        this.bindCustomThumbnails();
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        // 销毁Swiper实例
        this.swiperInstance?.destroy(true, true);
        this.swiperInstance = null;
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          // 关键修改：提取variant的纯颜色值（而非整个title）
          const selectedColor = this.extractColorFromVariant(variant);
          this.updateMedia(html, variant?.featured_media?.id, selectedColor);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          var variantId = variant.id
            , myPrice = variant.price
            , myPriceCom = variant.compare_at_price;

          // 计算折扣百分比
          let discountText = '';
          if (myPriceCom > myPrice && myPriceCom > 0) {
            // 计算百分比并四舍五入
            const discount = Math.round(((myPriceCom - myPrice) * 100) / myPriceCom);
            discountText = `${discount}% OFF`;
          }

          $(".show-more-media").show();
          $(".hide-more-media").hide();

          // 显示折扣
          $(".product_right_price").find(".save_label").text(discountText);
        };
      }

      // 新增：从variant中提取纯颜色值（适配多语言、多选项场景）
      extractColorFromVariant(variant) {
        if (!variant || !variant.options) return '';
        
        // 1. 先从product选项中找到颜色选项的索引
        const product = variant.product || {};
        const options = product.options || [];
        let colorOptionIndex = -1;
        
        for (let i = 0; i < options.length; i++) {
          const optionName = options[i]?.toLowerCase() || '';
          if (optionName.includes('color') || optionName.includes('颜色')) {
            colorOptionIndex = i;
            break;
          }
        }
        
        // 2. 提取颜色值并标准化
        if (colorOptionIndex >= 0 && variant.options[colorOptionIndex]) {
          return this.normalizeColor(variant.options[colorOptionIndex]);
        }
        
        // 降级方案：从title中提取（兼容旧逻辑，但标准化）
        return this.normalizeColor(variant.title);
      }

      // 新增：颜色值标准化（去空格、特殊字符、转小写）
      normalizeColor(color) {
        if (!color) return '';
        // 移除非字母/中文的字符，去空格，转小写
        return color
          .replace(/[^a-zA-Z\u4e00-\u9fa5]/g, ' ') // 保留字母和中文，其他替换为空格
          .replace(/\s+/g, '') // 移除所有空格
          .toLowerCase(); // 转小写
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId, selectedColor) {
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector('media-gallery ul');
        const customThumbsMobile = document.querySelector('.custom-thumbnails.pad_mobile .swiper-wrapper');
        const customThumbsPC = document.querySelector('.custom-thumbnails.pad_pc');

        if (!mediaGallerySource || !mediaGalleryDestination) return;

        // 标准化选中颜色
        const normalizedSelectedColor = this.normalizeColor(selectedColor);
        // 工具函数刷新媒体数据
        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
        const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
        const destinationSet = new Set(mediaGalleryDestinationItems.map((item) => item.dataset.mediaId));

        let shouldRefresh = false;

        // 1️⃣ 添加新媒体（仅添加颜色精确匹配的）
        for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
          const destItem = mediaGalleryDestinationItems[i];
          const mediaId = destItem.dataset.mediaId;

          // 提取并标准化图片颜色
          let mediaColor = (destItem.dataset.color || destItem.querySelector('img')?.alt || '')
            .split('__').pop()
            .replace(/-/g, ' ')
            .trim();
          mediaColor = this.normalizeColor(mediaColor);
          
          // 精确匹配：颜色相同 或 无颜色（兜底显示）
          const colorMatch = normalizedSelectedColor === '' || mediaColor === normalizedSelectedColor;
          
          if (!sourceSet.has(mediaId) && colorMatch) {
            mediaGallerySource.prepend(destItem);
            shouldRefresh = true;
          }
        }

        // 2️⃣ 删除无效媒体（非当前颜色匹配的）
        for (let i = 0; i < mediaGallerySourceItems.length; i++) {
          const item = mediaGallerySourceItems[i];
          const mediaId = item.dataset.mediaId;
          
          // 提取并标准化当前item的颜色
          let itemColor = (item.dataset.color || item.querySelector('img')?.alt || '')
            .split('__').pop()
            .replace(/-/g, ' ')
            .trim();
          itemColor = this.normalizeColor(itemColor);
          
          // 删除条件：不在目标列表 或 颜色不匹配（且有选中颜色）
          const shouldRemove = !destinationSet.has(mediaId) || 
                              (normalizedSelectedColor !== '' && itemColor !== normalizedSelectedColor);
          
          if (shouldRemove) {
            item.remove();
            shouldRefresh = true;
          }
        }

        // 3️⃣ 刷新数据
        if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

        // 4️⃣ 排序
        mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
          const sourceData = sourceMap.get(destinationItem.dataset.mediaId);
          if (sourceData && sourceData.index !== destinationIndex) {
            mediaGallerySource.insertBefore(
              sourceData.item,
              mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
            );
            [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          }
        });

        // 5️⃣ 设置活动图
        if (variantFeaturedMediaId) {
          mediaGallerySourceItems.forEach((item) => item.classList.remove('is-active'));
          const activeItem = mediaGallerySource.querySelector(`li[data-media-id="${this.dataset.section}-${variantFeaturedMediaId}"]`);
          if (activeItem) activeItem.classList.add('is-active');
        }

        // 6️⃣ 更新模态框
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;

        // 7️⃣ 同步 thumbnails（PC & Mobile）- 仅保留颜色匹配的
        if (customThumbsMobile) customThumbsMobile.innerHTML = '';
        if (customThumbsPC) customThumbsPC.innerHTML = '';

        // 筛选出颜色匹配的图片用于缩略图
        const matchedItems = mediaGallerySourceItems.filter(item => {
          let itemColor = (item.dataset.color || item.querySelector('img')?.alt || '')
            .split('__').pop()
            .replace(/-/g, ' ')
            .trim();
          itemColor = this.normalizeColor(itemColor);
          return normalizedSelectedColor === '' || itemColor === normalizedSelectedColor;
        });

        // 克隆到缩略图容器
        matchedItems.forEach((item, index) => {
          // 深度克隆并清理多余class
          const cloneMobile = item.cloneNode(true);
          const clonePC = item.cloneNode(true);

          // 重置class，只保留缩略图需要的
          cloneMobile.className = 'thumbnailsList swiper-slide';
          clonePC.className = 'thumbnailsList';
          // 保留data属性
          cloneMobile.dataset.mediaId = item.dataset.mediaId;
          clonePC.dataset.mediaId = item.dataset.mediaId;
          // 第一个缩略图默认选中
          if (index === 0) {
            cloneMobile.classList.add('is-active');
            clonePC.classList.add('is-active');
          }

          if (customThumbsMobile) customThumbsMobile.appendChild(cloneMobile);
          if (customThumbsPC) customThumbsPC.appendChild(clonePC);
        });

        // 绑定点击逻辑
        this.bindCustomThumbnails();
      }

      bindCustomThumbnails() {
        const gallery = this.querySelector(`#MediaGallery-${this.dataset.section}`);
        if (!gallery) return;

        // PC 缩略图
        const pcThumbs = this.querySelectorAll('.custom-thumbnails.pad_pc .thumbnailsList');
        pcThumbs.forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => {
            pcThumbs.forEach((t) => t.classList.remove('is-active'));
            thumbnail.classList.add('is-active');

            const mediaId = thumbnail.dataset.mediaId;
            const targetSlide = gallery.querySelector(`[data-media-id="${mediaId}"]`);
            if (targetSlide) {
              targetSlide.scrollIntoView({ behavior: 'smooth', block: 'start' });
              // 同步主图选中状态
              gallery.querySelectorAll('li[data-media-id]').forEach(li => li.classList.remove('is-active'));
              targetSlide.classList.add('is-active');
            }
          });
        });

        // Mobile 缩略图 (Swiper)
        const mobileThumbs = this.querySelectorAll('.custom-thumbnails.pad_mobile .thumbnailsList');
        mobileThumbs.forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => {
            mobileThumbs.forEach((t) => t.classList.remove('is-active'));
            thumbnail.classList.add('is-active');

            const mediaId = thumbnail.dataset.mediaId;
            const targetSlide = gallery.querySelector(`[data-media-id="${mediaId}"]`);
            if (targetSlide) {
              targetSlide.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest'
              });
              // 同步主图选中状态
              gallery.querySelectorAll('li[data-media-id]').forEach(li => li.classList.remove('is-active'));
              targetSlide.classList.add('is-active');
            }
          });
        });

        // 初始化Swiper - 避免重复初始化
        if (window.Swiper && !this.swiperInstance) {
          const swiperContainer = this.querySelector('.custom-thumbnails-swiper');
          console.log(swiperContainer, 111)
          if (swiperContainer) {
            this.swiperInstance = new Swiper(swiperContainer, {
              slidesPerView: 5,
              spaceBetween: 14,
              freeMode: true,
              slideToClickedSlide: true,
              on: {
                init() {
                  swiperContainer.style.visibility = 'visible';
                }
              }
            });
          }
        } else if (this.swiperInstance) {
          // 更新已存在的Swiper实例
          this.swiperInstance.update();
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}